import './campaignGuideHero.scss';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Header for the campaign guide, with the IMPACTS radar, the guide's headline, and the things IMPACTS measures.
 */
export const CampaignGuideHero = () => {
    const { t } = useTranslation();

    const measures = useMemo((): Array<{ key: MeasureIconKey, label: string, isHighlighted: boolean }> => [
        { key: 'cost', label: t('campaignGuide.measures.cost', 'Cost'), isHighlighted: false },
        { key: 'timeToImpact', label: t('campaignGuide.measures.timeToImpact', 'Time to impact'), isHighlighted: false },
        { key: 'behaviour', label: t('campaignGuide.measures.behaviour', 'Behaviour'), isHighlighted: false },
        { key: 'engagement', label: t('campaignGuide.measures.engagement', 'Engagement'), isHighlighted: true },
        { key: 'roi', label: t('campaignGuide.measures.roi', 'ROI'), isHighlighted: false },
    ], [t]);

    return (
        <div className="campaign-guide-hero">
            <Radar />
            <div className="campaign-guide-hero-eyebrow">
                {t('campaignGuide.hero.eyebrow', 'Campaign setup guide')}
            </div>
            <h1 className="campaign-guide-hero-heading">
                {t('campaignGuide.hero.headingStart', 'Most training stops at completion.')}
                <br />
                <span className="campaign-guide-hero-heading-accent">
                    {t('campaignGuide.hero.headingAccent', 'IMPACTS goes further.')}
                </span>
            </h1>
            <p className="campaign-guide-hero-introduction">
                {t('campaignGuide.introduction', 'A campaign is how IMPACTS tracks a training programme\'s real-world impact, from pre-assessment through to ROI. Five short stages, and you\'re live.')}
            </p>
            <div className="campaign-guide-hero-measures">
                {measures.map(measure => (
                    <div key={measure.key} className="campaign-guide-hero-measure">
                        <div className={`campaign-guide-hero-measure-badge ${measure.isHighlighted ? 'campaign-guide-hero-measure-badge-highlighted' : ''}`}>
                            <MeasureIcon iconKey={measure.key} />
                        </div>
                        <span>{measure.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Animated radar, the IMPACTS "target" motif, with a sweeping beam and blips.
 */
const Radar = () => (
    <div className="campaign-guide-hero-radar" aria-hidden="true">
        <svg viewBox="0 0 150 150">
            <defs>
                <linearGradient id="campaignGuideRadarSweep" x1="75" y1="4" x2="123" y2="23" gradientUnits="userSpaceOnUse">
                    <stop className="campaign-guide-hero-radar-sweep-start" />
                    <stop className="campaign-guide-hero-radar-sweep-end" offset="1" />
                </linearGradient>
            </defs>
            <circle className="campaign-guide-hero-radar-ring" cx="75" cy="75" r="71" strokeWidth="6" />
            <circle className="campaign-guide-hero-radar-ring" cx="75" cy="75" r="50" strokeWidth="6" />
            <circle className="campaign-guide-hero-radar-ring" cx="75" cy="75" r="29" strokeWidth="6" />
            <line className="campaign-guide-hero-radar-ring" x1="75" y1="0" x2="75" y2="150" strokeWidth="4" />
            <line className="campaign-guide-hero-radar-ring" x1="0" y1="75" x2="150" y2="75" strokeWidth="4" />
            <g className="campaign-guide-hero-radar-sweep">
                <path d="M75 75 L75 4 A71 71 0 0 1 123 23 Z" fill="url(#campaignGuideRadarSweep)" />
            </g>
            <circle className="campaign-guide-hero-radar-blip" cx="106" cy="50" r="4" style={{ animationDelay: '0s' }} />
            <circle className="campaign-guide-hero-radar-blip campaign-guide-hero-radar-blip-light" cx="48" cy="98" r="3.5" style={{ animationDelay: '0.8s' }} />
            <circle className="campaign-guide-hero-radar-blip" cx="100" cy="107" r="3" style={{ animationDelay: '1.5s' }} />
            <circle className="campaign-guide-hero-radar-centre" cx="75" cy="75" r="8" />
        </svg>
    </div>
);

type MeasureIconKey = 'cost' | 'timeToImpact' | 'behaviour' | 'engagement' | 'roi';

interface MeasureIconProps {
    iconKey: MeasureIconKey,
}

/**
 * Line icon for one of the things IMPACTS measures.
 */
const MeasureIcon = (props: MeasureIconProps) => {
    const { iconKey } = props;

    return (
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {measureIconShapes[iconKey]}
        </svg>
    );
};

const measureIconShapes: { [key in MeasureIconKey]: JSX.Element } = {
    // A pound sign, as costs and ROI in IMPACTS are shown in £.
    cost: <path d="M16 7.5A3.5 3.5 0 009.5 8.5V20M6.5 13h7M6.5 20h11" />,
    timeToImpact: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    behaviour: <path d="M12 21s-7-4.5-9.3-9A5.4 5.4 0 0112 6a5.4 5.4 0 019.3 6c-2.3 4.5-9.3 9-9.3 9z" />,
    engagement: <path className="campaign-guide-hero-measure-fill" d="M7 11v9H4a1 1 0 01-1-1v-7a1 1 0 011-1h3zm0 0l5-8 1 1v6h6a2 2 0 012 2l-1.5 6a2 2 0 01-2 1.5H10a3 3 0 01-3-3v-4.5z" />,
    roi: <path d="M4 20V10M10 20V4M16 20v-7M22 20v-3" />,
};
